import { observable, action, makeObservable } from 'mobx';
import _ from 'lodash';

import agent from '../agent';

import userStore from './userStore';
import { getDistanceZone } from '../../utils/commonMethods';

let addressThrottleTimer = null;

class TaskStore {
    currentList = {};
    currentEntity = {};
    updatingErrors = null;
    updating = false;
    loading = false;
    requestParams = null;
    deleteSuccess = false;
    currentTasksLookup = [];
    loadingLookup = false;
    lastListLoadTime = null;
    gpsNowLoading = false;

    constructor() {
        makeObservable(this, {
            currentList: observable,
            currentEntity: observable,
            updatingErrors: observable,
            updating: observable,
            loading: observable,
            requestParams: observable,
            deleteSuccess: observable,
            currentTasksLookup: observable,
            loadingLookup: observable,
            lastListLoadTime: observable,
            gpsNowLoading: observable,
            resetLastListLoadTime: action,
            filters: observable,
            appliedFilters: observable,
            setFilter: action,
            onFilter: action,
            loadList: action,
            returnDefaultNew: action,
            load: action,
            save: action,
            remove: action,
            loadLookup: action,
            performRecalcGpsForTask: action,
            recalcGpsForTask: action,
            loadTasksForCloning: action,
            cloneTasks: action,
            saveIntoIntegration: action
        });
    }

    resetLastListLoadTime() {
        this.lastListLoadTime = new Date();
    }

    filters = {
        status: '',
        name: '',
        project: '',
    };

    appliedFilters = {
        status: '',
        name: '',
        project: '',
    };

    setFilter(name, value) {
        const filters = Object.assign({}, this.filters);
        filters[name] = value;
        this.filters = filters;
    }

    onFilter() {
        this.appliedFilters = Object.assign({}, this.filters);
        // requestParams.filters = this.filters;
        // this.loadList(requestParams);
    }

    loadList(params) {
        return agent.Tasks.list(params)
            .then(
                action(list => {
                    this.requestParams = params;
                    list.time = new Date();
                    this.lastListLoadTime = list.time;
                    this.currentList = list;
                    return list;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    returnDefaultNew(currentUser) {
        this.currentEntity = {
            task: {
                assignee_id: currentUser && currentUser.user_type && currentUser.user_type === 'member' ? `${currentUser.id}` : '',
                name: null,
                data: {},
                start_time: 8 * 60,
                end_time: 17 * 60,
                scheduled_days: '0,1,2,3,4',
            },
        };
        this.loading = false;
    }

    load(id) {
        this.loading = true;
        return agent.Tasks.load(id)
            .then(
                action(response => {
                    if (!response.task.data) response.task.data = {};
                    if (response.task.Members && response.task.Members.length) {
                        response.task.assignee_id = response.task.Members.map(r => r.user_id).join(',');
                    } else {
                        response.task.assignee_id = '';
                    }

                    this.currentEntity = response;
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    save(values, isAdd) {
        this.updating = true;
        return agent.Tasks.save(values, isAdd)
            .then(
                action(user => {
                    this.currentEntity = user;
                    return user;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    throw err;
                })
            );
    }

    async remove(id) {
        await agent.Tasks.remove(id);
        this.deleteSuccess = true;
        return 1;
    }


    loadLookup(name) {
        return agent.Tasks.lookupByName(name)
            .then(
                action(list => {
                    this.currentTasksLookup = list;
                    return list;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    async performRecalcGpsForTask(newAddress, userInsteadOfTask, commonStore, t, taskId) {
        this.gpsNowLoading = true;
        let tsk;
        if (!userInsteadOfTask) {
            this.currentEntity.task.data.addressCache = newAddress;
            tsk = _.cloneDeep(this.currentEntity.task);
        } else {
            userStore.currentEntity.user.data.addressCache = newAddress;
            tsk = _.cloneDeep(userStore.currentEntity.user);
        }
        try {
            if (commonStore?.config?.client?.calculate_task_distance_with_company && !commonStore.config.client?.gps_data) {
                commonStore.addNotification(t('Please add the company address under company profile'), null, 'warning');
            }
            const data = await agent.Tasks.gpsAddress(newAddress, userInsteadOfTask ? null : taskId, !userInsteadOfTask);
            tsk.gps_data = data.result;
            tsk.data.addressCache = newAddress;
            tsk.distance_from_company_location = data.taskDistanceDetails;

            if (commonStore?.config?.client?.calculate_task_distance_with_company) {
                let getZone = ''
                if (tsk.distance_from_company_location) {
                    const distance = tsk.distance_from_company_location.split(' ')[0]
                    getZone = getDistanceZone(parseFloat(distance), commonStore.config.client.data.extendedRules.kilometersRules)
                    tsk.distance_type = getZone.distance;
                }
            }

            if (!userInsteadOfTask) {
                this.currentEntity = { task: tsk };
            } else {
                userStore.currentEntity = { user: tsk };
            }
            this.gpsNowLoading = false;
        } catch (e) {
            this.gpsNowLoading = false;
            throw e;
        }
    }

    async recalcGpsForTask(changes, userInsteadOfTask, commonStore, t) {
        const entityToAddressString = e => {
            return `${e.address}, ${e.post_place}, ${e.post_number}`;
        };

        if (!changes.address || !changes.post_place) {
            return changes;
        }

        const newAddress = entityToAddressString(changes);

        if (!userInsteadOfTask) {
            if (newAddress === this.currentEntity.task.data.addressCache) return changes;
        } else if (newAddress === userStore.currentEntity.user.data.addressCache) return changes;

        addressThrottleTimer = new Date().getTime();
        setTimeout(() => {
            // throttle
            if (new Date().getTime() - addressThrottleTimer < 1000) {
                return;
            }
            if (!userInsteadOfTask) {
                if (this.currentEntity.task.data.addressCache === newAddress) {
                    return;
                }
            } else if (userStore.currentEntity.user.data.addressCache === newAddress) {
                return;
            }

            this.performRecalcGpsForTask(newAddress, userInsteadOfTask, commonStore, t, changes.id ? changes.id : null);
        }, 1000);
        return changes;
    }

    async loadTasksForCloning(id) {
        return agent.Tasks.getTaskToClone(id).then(
            action(list => {
                return list;
            })
        )
            .catch(
                action(err => {
                    throw err;
                })
            )
    }

    async cloneTasks(data) {
        return agent.Tasks.cloneTasks(data).then(
            action(list => {
                return list;
            })
        )
            .catch(
                action(err => {
                    throw err;
                })
            )
    }
    saveIntoIntegration(values) {
        return agent.Tasks.saveIntoIntegration(values)
            .then(
                action(task => {
                    return task;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    throw err;
                })
            );
    }
}



const _TaskStore = new TaskStore();
export default _TaskStore;
